import { debounce } from 'lodash';
import { useMemo, useRef, useState } from 'react';

import InputAdornment from '@material-ui/core/InputAdornment';
import { TextInput } from '../TextInput';

import * as api from '../../api';
import * as logger from '../../logger';
import { parseValue } from 'common/dist/utils/types';

import hidePassword from '../../assets/hide-password.svg';
import showPassword from '../../assets/show-password.svg';

import styles from './index.module.css';
import defaultStyles from '../../styles/formSetStyles.module.css';

interface Props {
  value: string;
  updateFieldValue: (value: string) => void;
  saveFieldValue: (value: string) => void;
  formSetId: string;
  last4?: boolean;
}

enum QuestionStatus {
  Skipped = 'SKIPPED',
  Incomplete = 'INCOMPLETE',
  FilledOut = 'FILLED_OUT',
}

const PLACEHOLDER_SSN = '*********';
const PLACEHOLDER_SSN4 = '****';
const NUMBERS_REGEX = /^\d*$/;

function isValidSsn(ssn?: string, last4?: boolean): boolean {
  if (!ssn) return false;
  return last4 ? ssn.length === 4 : ssn.length === 9;
}

const SocialSecurity: React.FC<Props> = ({
  value,
  updateFieldValue,
  saveFieldValue,
  formSetId,
  last4 = false,
}) => {
  const placeholder = last4 ? PLACEHOLDER_SSN4 : PLACEHOLDER_SSN;
  const parsedValue = useMemo(
    () => parseValue<{ status: QuestionStatus }>(value),
    [value],
  );
  const [ssn, setSsn] = useState<string>(
    parsedValue?.status === QuestionStatus.FilledOut ? placeholder : '',
  );
  const [hideCharacters, setHideCharacters] = useState(true);
  const debouncedUpsert = useRef(
    debounce(
      async (ssn, formSetId) =>
        await api.client.PatientSsn.upsert(ssn, formSetId),
      500,
      { leading: true, trailing: true },
    ),
  );

  const lengthLimit = last4 ? 4 : 9;

  const saveValue = (status: QuestionStatus) => {
    let stringValue: string;
    try {
      stringValue = JSON.stringify({ status });
    } catch (err) {
      logger.error(err, 'Failed to transform status value for SSN');
      return;
    }
    updateFieldValue(stringValue);
    saveFieldValue(stringValue);
  };

  return (
    <div className={defaultStyles.InputDiv}>
      <TextInput
        id="social-security-number"
        value={ssn}
        onFocus={() => {
          if (ssn === placeholder) {
            // clear value if placeholder is in place
            setSsn('');
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <span
                className={styles.PasswordIcon}
                onClick={() => {
                  if (hideCharacters && ssn === placeholder) {
                    setSsn('');
                  }
                  setHideCharacters(!hideCharacters);
                }}
              >
                <img
                  alt="card logo"
                  src={hideCharacters ? hidePassword : showPassword}
                />
              </span>
            </InputAdornment>
          ),
        }}
        onChange={async ({ currentTarget }) => {
          // only allow number input
          const newValue = currentTarget.value;
          if (!NUMBERS_REGEX.exec(newValue)) return;
          if (newValue.length > lengthLimit) return;
          setSsn(newValue);

          if (!newValue && parsedValue?.status !== QuestionStatus.Skipped) {
            saveValue(QuestionStatus.Skipped);
            return;
          }

          if (!isValidSsn(newValue, last4)) {
            parsedValue?.status !== QuestionStatus.Incomplete &&
              saveValue(QuestionStatus.Incomplete);
            return;
          }

          await debouncedUpsert.current(newValue, formSetId);
          saveValue(QuestionStatus.FilledOut);
        }}
        type={hideCharacters ? 'password' : undefined}
      />
    </div>
  );
};

export default SocialSecurity;
